// ================================================================
// Documents API — آفر و فاکتور (org-scoped)
// الگوی apiRequest() مثل core/whatsapp.js
// ================================================================
// getToken() bleibt hier direkt importiert: die PDF-/Logo-Endpunkte unten liefern
// binäre Antworten (Blob), nicht JSON — die können nicht über apiRequest() laufen.
import { getToken } from './auth.js';
import { apiRequest, readErrorMessage } from './apiClient.js';

const base = '/api/documents';

// token-e kotah-omr baraye preview-e offer/rechnung dar iframe (org-scoped)
export function getOfferPreviewToken(docId) {
  return apiRequest('/api/voice/offer-preview-token', {
    method: 'POST',
    body: JSON.stringify({ offer_id: docId }),
  });
}
export const OFFER_PREVIEW_BASE = '/api/voice/offer-preview';

// ── سندها ──

// لیست همه‌ی سندهای سازمان
export function listDocuments() {
  return apiRequest(base);
}

// خواندن یک سند
export function getDocument(id) {
  return apiRequest(base + '/' + encodeURIComponent(id));
}

// EINE Position gezielt ändern (Preis/Menge) — mobile Offerten-Bearbeitung.
// Gleiche Engine wie das Voice-Preisdiktat; Totale werden serverseitig nachgezogen.
export function patchDocumentPosition(id, { position, unitPrice, quantity }) {
  return apiRequest(base + '/' + encodeURIComponent(id) + '/position', {
    method: 'PATCH',
    body: JSON.stringify({
      position,
      unitPrice: unitPrice ?? null,
      quantity: quantity ?? null,
    }),
  });
}

// NEUE Position ans Ende anhängen — mobiles «+» in der Offerten-Ansicht.
export function addDocumentPosition(id, { title, quantity, unit, unitPrice }) {
  return apiRequest(base + '/' + encodeURIComponent(id) + '/position', {
    method: 'POST',
    body: JSON.stringify({ title, quantity, unit: unit ?? null, unitPrice }),
  });
}

// MEHRERE Positionen in einem Zug — Voice: die beim Diktat genannten Arbeiten
// werden eingetragen, sobald der Entwurf (nach dem Kunden-Umweg) existiert.
export function addDocumentPositions(id, positions) {
  return apiRequest(base + '/' + encodeURIComponent(id) + '/positions', {
    method: 'POST',
    body: JSON.stringify({ positions }),
  });
}

// Gutschein-Code auf ein Dokument setzen (code) oder entfernen (null) —
// mobile Offerten-Ansicht. Prüfung/Deckel/Reservierung macht der Server.
export function setDocumentVoucher(id, code) {
  return apiRequest(base + '/' + encodeURIComponent(id) + '/voucher', {
    method: 'PATCH',
    body: JSON.stringify({ code: code ?? null }),
  });
}

// EINE Position löschen (1-basierte Nr) — mobiler Editor + Voice-Parität.
export function removeDocumentPosition(id, position) {
  return apiRequest(base + '/' + encodeURIComponent(id) + '/position/' + encodeURIComponent(position), {
    method: 'DELETE',
  });
}

// Zahlungsstatus einer Rechnung (manuell — keine Bankanbindung)
export function markDocumentPaid(id, paidAt) {
  return apiRequest(base + '/' + encodeURIComponent(id) + '/paid', {
    method: 'POST',
    body: JSON.stringify(paidAt ? { paidAt } : {}),
  });
}
export function markDocumentUnpaid(id) {
  return apiRequest(base + '/' + encodeURIComponent(id) + '/paid', { method: 'DELETE' });
}
// Fälligkeit («Zahlbar bis») einer Rechnung — Einzel-PATCH, damit der
// Mobile-Sheet nicht das komplette Dokument (PATCH /:id) schicken muss.
export function setDocumentDueDate(id, dueDate) {
  return apiRequest(base + '/' + encodeURIComponent(id) + '/due-date', {
    method: 'PATCH',
    body: JSON.stringify({ dueDate: dueDate || null }),
  });
}

// ساخت سند (مبالغ سمت سرور حساب می‌شوند)
// get-next-doc-number: shomare-ye khodkar-e badi baraye sanad
// «Gültig bis» einer Offerte (Rechnung: Fälligkeit) — Einzel-PATCH fürs Handy
export function setDocumentValidUntil(id, validUntil) {
  return apiRequest(base + "/" + encodeURIComponent(id) + "/valid-until", {
    method: 'PATCH',
    body: JSON.stringify({ validUntil: validUntil || null }),
  });
}

// Umzugs-Angaben (Termine, Adressen, Zahlungsart, Personal, Inventar) —
// Teil-PATCH: nur mitgeschickte Felder ändern sich. Backend: move-details.ts
export function patchMoveDetails(id, data) {
  return apiRequest(base + "/" + encodeURIComponent(id) + "/move-details", {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function getNextDocumentNumber(type) {
  return apiRequest(base + '/next-number?type=' + encodeURIComponent(type || 'OFFER'));
}

export function createDocument(data) {
  return apiRequest(base, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Leerer Entwurf für einen bestehenden Kunden — mobiles «+» (Offerte von Hand).
// Nur die contactId geht raus; Name/Adresse holt der Server org-scoped aus dem
// Kontakt. Positionen kommen danach über addDocumentPosition() dazu.
export function createBlankDraft({ contactId, type = 'OFFER', title = null }) {
  return apiRequest(base + '/blank-draft', {
    method: 'POST',
    body: JSON.stringify({ contactId, type, title }),
  });
}

// ویرایش سند (PATCH) — backend lock ra check mikonad
export function updateDocument(id, data) {
  return apiRequest(base + '/' + encodeURIComponent(id), {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// حذف سند (DELETE) — soft-delete -> satl-e ashghal; backend lock ra check mikonad
export function deleteDocument(id) {
  return apiRequest(base + '/' + encodeURIComponent(id), {
    method: 'DELETE',
  });
}

// ── Abschlagsrechnungen (Teil-/Akontorechnungen) — Kette ausgehend von einem Angebot ──
export function getPartialInvoiceChain(offerId) {
  return apiRequest(base + '/' + encodeURIComponent(offerId) + '/partial-invoice-chain');
}

export function createPartialInvoice(offerId, data) {
  return apiRequest(base + '/' + encodeURIComponent(offerId) + '/partial-invoices', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function createFinalInvoice(offerId) {
  return apiRequest(base + '/' + encodeURIComponent(offerId) + '/final-invoice', {
    method: 'POST',
  });
}

// ── Angebot 1:1 in eine Rechnung umwandeln (ein Klick, alle Positionen) ──
export function createInvoiceFromOffer(offerId) {
  return apiRequest(base + '/' + encodeURIComponent(offerId) + '/invoice', {
    method: 'POST',
  });
}

// ── Nachkalkulation — Soll (Kalkulation) vs. Ist (Zeiterfassung) je Angebot ──
export function getNachkalkulation(offerId) {
  return apiRequest(base + '/' + encodeURIComponent(offerId) + '/nachkalkulation');
}

// لیست سطل آشغال (sanad-haye hazf-shode)
export function listTrash() {
  return apiRequest(base + '/trash');
}

// بازگردانی از سطل آشغال
export function restoreDocument(id) {
  return apiRequest(base + '/' + encodeURIComponent(id) + '/restore', {
    method: 'POST',
  });
}

// حذف دائمی از سطل آشغال
export function purgeDocument(id) {
  return apiRequest(base + '/' + encodeURIComponent(id) + '/purge', {
    method: 'DELETE',
  });
}

// خالی کردن کامل سطل آشغال (همه یکباره)
export function emptyTrash() {
  return apiRequest(base + '/trash/empty', {
    method: 'DELETE',
  });
}

// ── View PDF (blob baraye modal preview) — lock NEMIKONAD ──
// endpoint /pdf hala faghat misazad (lock:false). object URL barmigardanad.
export async function viewDocumentPdf(id) {
  const token = getToken();
  const res = await fetch(base + '/' + encodeURIComponent(id) + '/pdf', {
    headers: { ...(token ? { Authorization: 'Bearer ' + token } : {}) },
  });
  if (!res.ok) {
    let body = {};
    try { body = await res.json(); } catch { /* binäre Antwort ohne JSON-Body */ }
    const err = new Error(readErrorMessage(body, res.status));
    err.status = res.status;
    err.code = body?.error?.code;
    throw err;
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob); // call-site bayad revokeObjectURL konad
}

// ── Download PDF (binary) — Rechnung ra LOCK mikonad (endpoint /download) ──
export async function downloadDocumentPdf(id, number) {
  const token = getToken();
  const res = await fetch(base + '/' + encodeURIComponent(id) + '/download', {
    headers: { ...(token ? { Authorization: 'Bearer ' + token } : {}) },
  });
  if (!res.ok) {
    let body = {};
    try { body = await res.json(); } catch { /* binäre Antwort ohne JSON-Body */ }
    const err = new Error(readErrorMessage(body, res.status));
    err.status = res.status;
    err.code = body?.error?.code;
    throw err;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = (number || 'dokument') + '.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

// ── Nachweis der Zusage ────────────────────────────────────
// Zeitpunkt, IP, Gerät, Fingerabdruck der PDF und die abgehakte AGB-Fassung.
export function getAcceptanceProof(id) {
  return apiRequest(base + '/' + encodeURIComponent(id) + '/acceptance');
}

// Die ARCHIVIERTE, angenommene PDF — genau die Datei, deren Fingerabdruck im
// Nachweis steht. Bewusst nicht neu erzeugt (das ergäbe einen anderen Hash).
export async function downloadAcceptedPdf(id, number) {
  const token = getToken();
  const res = await fetch(base + '/' + encodeURIComponent(id) + '/accepted-pdf', {
    headers: { ...(token ? { Authorization: 'Bearer ' + token } : {}) },
  });
  if (!res.ok) {
    let body = {};
    try { body = await res.json(); } catch { /* binäre Antwort ohne JSON-Body */ }
    const err = new Error(readErrorMessage(body, res.status));
    err.status = res.status;
    err.code = body?.error?.code;
    throw err;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = (number || 'dokument') + '.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

// Link-Vorschaukarte vorbereiten, BEVOR gesendet wird.
// Ohne das entsteht die Karte erst, wenn der Vorschau-Roboter von WhatsApp den
// Link abholt — also genau dann, wenn die Nachricht beim Kunden ankommt. Dieser
// erste Aufruf dauert auf dem Server 3–5 Sekunden; wartet der Roboter nicht so
// lange, sieht der Kunde gar keine Karte und WhatsApp fragt nie wieder nach.
//
// Wird beim ÖFFNEN des Versand-Dialogs gerufen, nicht beim Senden: bis der
// Nutzer Kanal und Empfänger gewählt hat, ist die Karte meist längst fertig.
//
// Wirft nie: eine fehlende Vorschau darf den Versand nicht aufhalten.
export function warmDocumentPreviewCard(id) {
  return apiRequest(base + '/' + encodeURIComponent(id) + '/preview-card', {
    method: 'POST',
    body: JSON.stringify({}),
  }).catch(() => ({ ready: false }));
}

// ارسال سند با ایمیل
export function sendDocumentEmail(id, payload) {
  return apiRequest(base + '/' + encodeURIComponent(id) + '/send-email', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ارسال سند با واتساپ
// WhatsApp-Versand per LINK: der Server legt Token + Kundenseite an, baut den
// fertigen Text und meldet die Adresse zurück, mit der die App WhatsApp öffnet.
// Die Datei geht bewusst NICHT mit — über Klick-zu-Chat lässt sich nur Text
// vorbereiten; die PDF liegt hinter dem Link.
export function getWhatsappShareLink(id, phone) {
  return apiRequest(base + '/' + encodeURIComponent(id) + '/whatsapp-link', {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
}

export function sendDocumentWhatsApp(id, payload) {
  return apiRequest(base + '/' + encodeURIComponent(id) + '/send-whatsapp', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ── Firmenprofil (تنظیمات شرکت) ──

export function getCompanyProfile() {
  return apiRequest('/api/company-profile');
}

// آپلود لوگو (FormData، نه JSON)
export async function uploadCompanyLogo(file) {
  const token = getToken();
  const fd = new FormData();
  fd.append('logo', file);
  const res = await fetch('/api/company-profile/logo', {
    method: 'POST',
    headers: { ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: fd,
  });
  let body = {};
  try { body = await res.json(); } catch { /* no body */ }
  if (!res.ok) {
    const err = new Error(readErrorMessage(body, res.status));
    err.status = res.status;
    err.code = body?.error?.code;
    throw err;
  }
  return body; // { ok, logoUrl }
}

// گرفتن لوگوی فعلی به‌صورت blob URL (برای preview در صفحه، با auth)
export async function fetchCompanyLogoBlob() {
  const token = getToken();
  const res = await fetch('/api/company-profile/logo', {
    headers: { ...(token ? { Authorization: 'Bearer ' + token } : {}) },
  });
  if (!res.ok) return null; // logo nadarad
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export function saveCompanyProfile(data) {
  return apiRequest('/api/company-profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}
